import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { SecondaryButton, PrimaryButton, Button as ButtonTypography, ButtonArrowState } from '@huggg/react-components'

import { Map } from 'components';
import { AsyncImage, Dialog, SvgIcon } from 'components/ui';
import { ScreenReaderOnly } from 'components/misc';
import { withLocations } from 'hoc';

import GoogleMapsService from 'services/GoogleMapsService';
import AnalyticsService from 'services/AnalyticsService';
import { PlaceholderMap } from './PlaceholderMap'

import styles from './locations-dialog.module.scss';
import config from 'config';
import dictionary from 'config/dictionary.json';

const analytics = new AnalyticsService();
const TMP_BRAND_NAME_MAP = new window.Map([
  ['consolidated-coffee', ['independent coffee locations']],
  ['consolidated-gelato', ['gelato locations']],
  ['caffe-nero', ['Caffè Nero']],
  ['crosstown', ['Crosstown Doughnuts']],
  ['friska', ['Friska']],
  ['konditor', ['Konditor and Cook']],
  ['lolas', ["Lola's Cupcakes"]],
  ['mrs-potts', ['Mrs. Potts Chocolate House']],
  ['odeon', ['Odeon Cinemas']],
  ['pinkmans', ['Pinkmans']],
  ['swoon', ['Swoon Gelato']],
  ['vue', ['Vue Cinemas']]
]);

export class LocationsDialog extends Component {
  constructor(props) {
    super(props);

    this.modalRef = createRef();
    this.placeholderRef = createRef();

    this.state = {
      mapBooted: false,
    };

    this.GoogleMapsService = new GoogleMapsService();
    this.onPlaceholderClick = this.onPlaceholderClick.bind(this);
    this.onOpenButtonClick = this.onOpenButtonClick.bind(this);
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this);
  }

  componentDidMount() {
    this.GoogleMapsService.initialise().then(() => {
      this.modalRef.current.open();
      this.modalRef.current.close();
      setTimeout(() => this.setState({ mapBooted: true }), 50);
    });
  }

  componentWillUnmount() {
    this.mounted = false;
    this.GoogleMapsService = null;
  }

  getPlaceholderURL(brandName) {
    const baseURL = `${config.CDN}/maps/placeholder`;
    let name = null;

    TMP_BRAND_NAME_MAP.forEach((value, key) => {
      if (value.indexOf(brandName) > -1) {
        name = key;
      }
    });

    if(!Boolean(name)) { return null }

    return {
      full: `${baseURL}/${name}.jpg`,
      min: `${baseURL}/${name}.min.jpg`
    };
  }

  onPlaceholderClick(event) {
    event.preventDefault();
    this.modalRef.current.open();
  }

  onOpenButtonClick() {
    this.modalRef.current.open();
  }

  onCloseButtonClick() {
    this.modalRef.current.close();
  }

  onAfterOpen() {
    analytics.dialogViewedLocationsMap();
  }

  render() {
    const {
      className,
      showPlaceholder,
      buttonPosition,
      brandName
    } = this.props;
    const buttonClassName = classnames({
      [styles.triggerButton]: showPlaceholder,
      [styles.triggerButtonTop]: showPlaceholder && buttonPosition === 'top',
      [styles.triggerButtonBottom]:
        showPlaceholder && buttonPosition === 'bottom'
    });

    const placeholderImgs = this.getPlaceholderURL(brandName)

    const containerClassName = classnames(
      className,
      'container',
      styles.container,
      {
        [styles.containerExtraMarginTop]:
          showPlaceholder && buttonPosition === 'top'
      }
    );

    const renderBrandPlaceholder = () => (
      <AsyncImage
        src={placeholderImgs.full}
        placeholder={placeholderImgs.min}
        alt="Map placeholder depicting potential redemption locations"
        className={classnames('image', styles.placeholder)}
      />
    )

    const renderGenericPlaceholder = () => (
      <PlaceholderMap />
    )

    return (
      <div
        className={containerClassName}
        data-test-id="locations-dialog-container"
      >
        {showPlaceholder && (
          <button
            className={styles.triggerPlaceholder}
            onClick={this.onPlaceholderClick}
            disabled={!this.state.mapBooted}
            ref={this.placeholderRef}
            type="button"
            data-test-id="locations-dialog-trigger-placeholder"
          >
            {Boolean(placeholderImgs) ? renderBrandPlaceholder() : renderGenericPlaceholder() }
          </button>
        )}
        <SecondaryButton
          className={buttonClassName}
          disabled={!this.state.mapBooted}
          onClick={this.onOpenButtonClick}
        >
          <SvgIcon name="location" className={styles.triggerButtonIcon} />
          <ButtonTypography className={styles.buttonCopy}>{dictionary.redemptionLocations}</ButtonTypography>
        </SecondaryButton>

        <Dialog
          hidden={!this.state.mapBooted}
          ref={this.modalRef}
          ariaLabelledBy="redemption-locations"
          data-test-id="locations-dialog"
          contentClassName={styles.dialog}
          closeButton={
            <PrimaryButton
              className={styles.closeButton}
              onClick={this.onCloseButtonClick}
              arrow={ButtonArrowState.Backwards}
            />
          }
        >
          <ScreenReaderOnly
            srComponent={
              <div id="redemption-locations">Redemption Locations Map</div>
            }
          />
          <Map locations={this.props.locations} className={styles.map} googleMapService={this.GoogleMapsService} fetchUserLocation={this.state.mapBooted} />
        </Dialog>
      </div>
    );
  }
}

LocationsDialog.defaultProps = {
  buttonPosition: 'top',
  showPlaceholder: true
};

LocationsDialog.propTypes = {
  buttonPosition: PropTypes.oneOf(['top', 'bottom']),
  brandName: PropTypes.string,
  className: PropTypes.string,
  locations: PropTypes.array,
  showPlaceholder: PropTypes.bool
};

export default withLocations(LocationsDialog);
